// Key Accounts Daily feeds — Dashmote admins edit a Daily feed's config: the
// crawl location, SKUs, key accounts, formats, promo calendar and matching
// rules. The draft is plain JSON. Check runs the server's validator, Save shows
// the changes and asks to confirm, and every save keeps the version it
// replaced, so History can load one of the latest replaced versions back
// into the editor.
// Data: the `dashoneKeyAccountsDailyConfig` Firebase function.
import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Button,
  Empty,
  Input,
  List,
  Modal,
  Result,
  Space,
  Spin,
  Tag,
  theme,
} from "antd";
import { useAdminStatus } from "../features/dashai/hooks/useAdminStatus";
import { besideSidebar } from "../layout";
import {
  CheckOutlined,
  HistoryOutlined,
  PlusOutlined,
  ReloadOutlined,
} from "@ant-design/icons";
import {
  checkDailyFeed,
  dailyFeedHistory,
  getDailyFeed,
  listDailyFeeds,
  saveDailyFeed,
} from "../api/keyAccountsDailyApi";
import { lineDiff } from "../utils/lineDiff";

const pretty = (v) => JSON.stringify(v, null, 2);
const feedKey = (f) => `${f.workspace}/${f.country}`;

/** The draft as an object, or the reason it is not JSON. */
function parseDraft(text) {
  try {
    return { config: JSON.parse(text) };
  } catch (err) {
    return { error: `Not valid JSON: ${err.message}` };
  }
}

function DiffView({ rows }) {
  const { token } = theme.useToken();
  const bg = { add: token.colorSuccessBg, del: token.colorErrorBg };
  const mark = { add: "+", del: "−", same: " ", gap: "⋯" };
  return (
    <pre
      data-testid="daily-diff"
      className="max-h-[60vh] overflow-auto rounded border border-border text-[12px] leading-[18px]"
    >
      {rows.map((r, i) => (
        <div
          key={i}
          style={{ background: bg[r.kind] }}
          className="px-2 whitespace-pre"
        >
          {mark[r.kind]} {r.text}
        </div>
      ))}
    </pre>
  );
}

/** The feeds as a list to open, or their loading, failed or empty state. */
function FeedList({ feeds, openKey, onRetry, onOpen }) {
  if (feeds.status === "loading") return <Spin />;
  if (feeds.status === "error") {
    return (
      <Alert
        type="error"
        message="The feeds could not be loaded"
        description={feeds.error}
        action={<Button onClick={onRetry}>Retry</Button>}
      />
    );
  }
  if (feeds.list.length === 0)
    return <Empty description="No Daily feeds yet" />;
  return (
    <List
      size="small"
      bordered
      dataSource={feeds.list}
      renderItem={(f) => (
        <List.Item
          className={`cursor-pointer ${openKey === feedKey(f) ? "bg-muted" : ""}`}
          onClick={() => onOpen(f)}
        >
          <div className="flex w-full items-center justify-between gap-2">
            <span>
              <b>{f.workspace}</b> · {f.country}
              <div className="text-[12px] text-muted-foreground">
                {f.brand} on {f.platform}
              </div>
            </span>
            <Tag color={f.enabled ? "green" : "default"}>
              {f.enabled ? "On" : "Off"}
            </Tag>
          </div>
        </List.Item>
      )}
    />
  );
}

/** The versions saves replaced, each loadable back into the editor. */
function HistoryList({ entries, onLoad }) {
  return (
    <List
      size="small"
      bordered
      header="Replaced versions, latest first"
      locale={{ emptyText: "No saved changes yet" }}
      dataSource={entries}
      renderItem={(h) => (
        <List.Item
          actions={[
            <Button key="load" size="small" onClick={() => onLoad(h)}>
              Load into editor
            </Button>,
          ]}
        >
          Replaced{" "}
          {h.replacedAt ? new Date(h.replacedAt).toLocaleString() : "—"} by{" "}
          {h.replacedBy?.email || h.replacedBy?.uid || "unknown"}
        </List.Item>
      )}
    />
  );
}

// The route admits any staff member, and the feed function admits Dashmote admins only.
export default function KeyAccountsDailyConfigPage() {
  const { isAdmin, loading } = useAdminStatus();
  // Each staff page clears the fixed sidebar itself.
  return (
    <div style={besideSidebar} className="min-h-[100vh]">
      {loading ? (
        <Spin className="m-6" />
      ) : isAdmin ? (
        <DailyFeedsEditor />
      ) : (
        <Result
          status="403"
          title="Dashmote admins only"
          subTitle="Ask for the Dashmote admin role to edit the Key Accounts Daily feeds."
        />
      )}
    </div>
  );
}

function DailyFeedsEditor() {
  const [modal, modalHolder] = Modal.useModal();
  const [feeds, setFeeds] = useState({
    status: "loading",
    list: [],
    error: "",
  });
  const [open, setOpen] = useState(null); // { workspace, country, stored, version, isNew }
  const [draft, setDraft] = useState("");
  const [check, setCheck] = useState(null); // { ok, error?, rebuildsDays, for: draft }
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [history, setHistory] = useState(null);
  const [confirm, setConfirm] = useState(false);
  const [creating, setCreating] = useState(null); // { workspace, country }

  const loadFeeds = useCallback(async () => {
    setFeeds((f) => ({ ...f, status: "loading", error: "" }));
    try {
      setFeeds({ status: "ready", list: await listDailyFeeds(), error: "" });
    } catch (err) {
      setFeeds({ status: "error", list: [], error: err.message });
    }
  }, []);
  useEffect(() => {
    loadFeeds();
  }, [loadFeeds]);

  const openFeed = useCallback(async (workspace, country) => {
    setBusy(true);
    setError("");
    setCheck(null);
    setHistory(null);
    try {
      const { config, version } = await getDailyFeed(workspace, country);
      setOpen({
        workspace,
        country,
        stored: pretty(config),
        version,
        isNew: false,
      });
      setDraft(pretty(config));
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  }, []);

  const startNew = () => {
    const { workspace, country } = creating;
    // The copy leaves out the v1 block: its store and SKU weights belong to one market,
    // so a new feed switches Version 1 on with its own block.
    const { v1: _v1, ...base } = parseDraft(draft).config ?? {};
    const text = pretty({ ...base, workspace, country, enabled: false });
    setOpen({ workspace, country, stored: "", version: null, isNew: true });
    setDraft(text);
    setCheck(null);
    setHistory(null);
    setCreating(null);
  };

  const changed = open && draft !== open.stored;
  const checkedNow = check && check.for === draft;
  // Opening another feed or reloading replaces the draft, so an unsaved change asks first.
  const discardThen = (next) =>
    changed
      ? modal.confirm({
          title: "Discard your unsaved changes?",
          okText: "Discard",
          onOk: next,
        })
      : next();

  const runCheck = async () => {
    const parsed = parseDraft(draft);
    if (parsed.error) {
      setCheck({
        ok: false,
        error: parsed.error,
        rebuildsDays: false,
        for: draft,
      });
      return;
    }
    setBusy(true);
    try {
      const r = await checkDailyFeed(
        open.workspace,
        open.country,
        parsed.config,
      );
      setCheck({ ...r, for: draft });
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  const save = async () => {
    setBusy(true);
    setError("");
    try {
      const { config } = parseDraft(draft);
      await saveDailyFeed(open.workspace, open.country, config, open.version);
      setConfirm(false);
      await openFeed(open.workspace, open.country);
      await loadFeeds();
    } catch (err) {
      setConfirm(false);
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  const showHistory = async () => {
    setBusy(true);
    try {
      setHistory(await dailyFeedHistory(open.workspace, open.country));
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  const diff = useMemo(
    () =>
      confirm && open
        ? lineDiff(open.stored, draft, { changedOnly: true })
        : [],
    [confirm, open, draft],
  );

  return (
    <div className="flex flex-col gap-4 p-6">
      {modalHolder}
      <div>
        <h1 className="text-[20px] font-semibold">Key Accounts Daily feeds</h1>
        <p className="text-muted-foreground">
          Each feed sets one workspace and market&apos;s Daily page: the crawl,
          the SKUs, the key accounts and the promo calendar. A change to the
          brand, SKU rules or chains rebuilds the stored crawl days at the next
          scheduled run, at 06:00 or 15:00 Dubai.
        </p>
      </div>
      <div className="grid grid-cols-[280px_1fr] gap-6">
        <div className="flex flex-col gap-2">
          <Space>
            <Button
              icon={<ReloadOutlined />}
              onClick={loadFeeds}
              aria-label="Reload feeds"
            />
            <Button
              icon={<PlusOutlined />}
              disabled={!open}
              onClick={() =>
                setCreating({ workspace: open?.workspace ?? "", country: "" })
              }
            >
              New feed from this one
            </Button>
          </Space>
          <FeedList
            feeds={feeds}
            openKey={open ? feedKey(open) : ""}
            onRetry={loadFeeds}
            onOpen={(f) => discardThen(() => openFeed(f.workspace, f.country))}
          />
        </div>

        <div className="flex flex-col gap-3">
          {error && (
            <Alert
              type="error"
              message={error}
              closable
              onClose={() => setError("")}
              action={
                open &&
                !open.isNew && (
                  <Button
                    size="small"
                    onClick={() =>
                      discardThen(() => openFeed(open.workspace, open.country))
                    }
                  >
                    Reload feed
                  </Button>
                )
              }
            />
          )}
          {!open ? (
            <Empty description="Pick a feed to edit its config" />
          ) : (
            <>
              <div className="flex items-center justify-between">
                <h2 className="text-[16px] font-semibold">
                  {open.workspace} · {open.country}
                  {open.isNew && <Tag className="ml-2">New</Tag>}
                </h2>
                <Space>
                  {!open.isNew && (
                    <Button
                      icon={<HistoryOutlined />}
                      onClick={showHistory}
                      disabled={busy}
                    >
                      History
                    </Button>
                  )}
                  <Button
                    onClick={() => setDraft(open.stored)}
                    disabled={!changed || busy}
                  >
                    Discard changes
                  </Button>
                  <Button
                    icon={<CheckOutlined />}
                    onClick={runCheck}
                    disabled={busy}
                  >
                    Check
                  </Button>
                  <Button
                    type="primary"
                    onClick={() => setConfirm(true)}
                    disabled={!changed || !checkedNow || !check.ok || busy}
                  >
                    Save
                  </Button>
                </Space>
              </div>
              {checkedNow &&
                (check.ok ? (
                  <Alert
                    type="success"
                    message={
                      check.rebuildsDays
                        ? "Valid. Saving rebuilds the stored crawl days at the next scheduled run, at 06:00 or 15:00 Dubai."
                        : "Valid."
                    }
                  />
                ) : (
                  <Alert type="error" message={check.error} />
                ))}
              <Input.TextArea
                aria-label="Feed config"
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                autoSize={{ minRows: 24, maxRows: 48 }}
                spellCheck={false}
                className="font-mono text-[12px]"
              />
              {history && (
                <HistoryList
                  entries={history}
                  onLoad={(h) => setDraft(pretty(h.config))}
                />
              )}
            </>
          )}
        </div>
      </div>

      <Modal
        open={confirm}
        title={`Save ${open ? `${open.workspace} · ${open.country}` : ""}?`}
        okText="Save"
        confirmLoading={busy}
        onOk={save}
        onCancel={() => setConfirm(false)}
        width={800}
      >
        <p>
          {check?.rebuildsDays
            ? "This change rebuilds the stored crawl days at the next scheduled run, at 06:00 or 15:00 Dubai."
            : "The Daily page shows this config on its next load."}
        </p>
        <DiffView rows={diff} />
      </Modal>

      <Modal
        open={!!creating}
        title="New Daily feed"
        okText="Start editing"
        okButtonProps={{
          disabled:
            !/^[A-Za-z0-9_-]+$/.test(creating?.workspace ?? "") ||
            !/^[A-Z]{2}$/.test(creating?.country ?? ""),
        }}
        onOk={startNew}
        onCancel={() => setCreating(null)}
      >
        <p>The new feed starts as a copy of the open one, switched off.</p>
        <Space direction="vertical" className="w-full">
          <Input
            aria-label="Workspace"
            placeholder="Workspace id, such as red_bull_global"
            value={creating?.workspace ?? ""}
            onChange={(e) =>
              setCreating((c) => ({ ...c, workspace: e.target.value.trim() }))
            }
          />
          <Input
            aria-label="Market"
            placeholder="Market code, such as SA"
            value={creating?.country ?? ""}
            onChange={(e) =>
              setCreating((c) => ({
                ...c,
                country: e.target.value.trim().toUpperCase(),
              }))
            }
          />
        </Space>
      </Modal>
    </div>
  );
}
